// accordianShipping.ts
import { windowWidth } from './utils';

type AccordionType = 'one-open' | 'multi-open';

const accordianShipping = {
  init() {
    this.bindDelegatedClick();
    this.bindReset();
    this.bindMutationObserver();
    this.syncAll(); // initial
  },

  // ✅ Only shipping accordions
  getAccordions() {
    return document.querySelectorAll('.jsAccordianShipping') as NodeListOf<HTMLElement>;
  },

  bindReset() {
    // Dedicated event so it won't interfere with existing accordion module
    // Call after shipping calc AJAX updates:
    // window.dispatchEvent(new Event('shipping:reset'));
    window.addEventListener('shipping:reset', () => {
      this.syncAll();
    });
  },

  bindDelegatedClick() {
    // Prevent double-binding if init runs more than once
    if ((document.documentElement).dataset.accordionShippingDelegated === 'true') return;
    (document.documentElement).dataset.accordionShippingDelegated = 'true';

    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      const header = target.closest('.jsAccordianItem') as HTMLElement | null;
      if (!header) return;

      // ✅ Only handle clicks inside shipping accordion wrappers
      const wrap = header.closest('.jsAccordianShipping') as HTMLElement | null;
      if (!wrap) return;

      const width = wrap.dataset.width ? wrap.dataset.width : '999999';
      if (windowWidth >= parseInt(width, 10)) return;

      const content = header.nextElementSibling as HTMLElement | null;
      if (!content) return;

      e.preventDefault();

      const type = (wrap.dataset.type as AccordionType) || 'multi-open';
      const isOpen = header.dataset.open === 'true';

      // For one-open accordions, close siblings within THIS wrap
      if (!isOpen && type === 'one-open') {
        const headers = wrap.querySelectorAll('.jsAccordianItem') as NodeListOf<HTMLElement>;
        headers.forEach((h) => {
          if (h === header) return;
          this.close(wrap, h);
        });
      }

      if (!isOpen) this.open(wrap, header, content);
      else this.close(wrap, header);
    });
  },

  bindMutationObserver() {
    if ((document.documentElement).dataset.accordionShippingObserver === 'true') return;
    (document.documentElement).dataset.accordionShippingObserver = 'true';

    const mo = new MutationObserver((mutations) => {
      // If changes happen inside a shipping accordion, resync heights.
      for (const m of mutations) {
        const t = m.target as HTMLElement | null;
        if (t?.closest?.('.jsAccordianShipping')) {
          this.syncAll();
          break;
        }
      }
    });

    mo.observe(document.documentElement, { subtree: true, childList: true, characterData: true });
  },

  open(wrap: HTMLElement, header: HTMLElement, content: HTMLElement) {
    header.dataset.open = 'true';
    header.classList.add('activeClass');
    wrap.classList.add('activeClass');

    wrap.style.maxHeight = header.offsetHeight + content.scrollHeight + 'px';
  },

  close(wrap: HTMLElement, header: HTMLElement) {
    header.dataset.open = 'false';
    header.classList.remove('activeClass');
    wrap.classList.remove('activeClass');

    wrap.style.maxHeight = header.offsetHeight + 'px';
  },

  syncAll() {
    this.getAccordions().forEach((wrap) => {
      const width = wrap.dataset.width ? wrap.dataset.width : '999999';
      if (windowWidth >= parseInt(width, 10)) return;

      const header = wrap.querySelector('.jsAccordianItem') as HTMLElement | null;
      if (!header) return;

      const content = header.nextElementSibling as HTMLElement | null;
      if (!content) return;

      const isOpen = header.dataset.open === 'true';

      wrap.style.maxHeight = isOpen
        ? header.offsetHeight + content.scrollHeight + 'px'
        : header.offsetHeight + 'px';
    });
  },
};

export default accordianShipping;